import { apiSearchResults } from '../api.ts'
import type { ISearchStore } from '../types.ts'
import { UPDATES_SEARCH_TAB, DEFAULT_RECORDS_PER_PAGE, UPDATES_SEARCH_SORT_OPTIONS, UPDATES_SEARCH_SORT_DEFAULT } from '../constants.ts'
import { formatDate, getYearDateRange } from '@/utils/dateHelpers';

export default {
  resetUpdatesFiltersForMainSearch: function(this: ISearchStore, facetFilter: boolean = false) {
    // reset all search filters except document number, if search results are displayed
    //@ts-ignore
    if (this.isResultLoaded(UPDATES_SEARCH_TAB)) {
      this.clearSearchBarFilters(UPDATES_SEARCH_TAB, []);
    }
    if(!facetFilter) {
      // reset all facet filters, apply only the search bar filters
      this.clearSearchFacetFilters(UPDATES_SEARCH_TAB, []);
    }
    
  },
  getUpdatesSearchParams: function(this: ISearchStore, facetFilter: boolean = false, pageNo: number = 1 ) {
    this.resetUpdatesFiltersForMainSearch(facetFilter);

    //use search bar filter values
    let filterPrefix = `${UPDATES_SEARCH_TAB}`;
    //if facetFilter is true, use the facetFilter values
    if(facetFilter) {
      filterPrefix = `${UPDATES_SEARCH_TAB}.facetFilter`;
    }

    const { start: defaultStart, end: defaultEnd } = getYearDateRange();
    const dateStart = this[`${filterPrefix}.dateStart`] == null ? defaultStart : this[`${filterPrefix}.dateStart`];
    const dateEnd = this[`${filterPrefix}.dateEnd`] == null ? defaultEnd : this[`${filterPrefix}.dateEnd`];

    let params = {
      'filter-publisher':  (this[`${filterPrefix}.publisher`] || []).map((p: { id: any }) => String(p.id)).join(','),
      'filter-what': (this[`${filterPrefix}.what`] || []).map((p: { id: any }) => String(p.id)).join(','),
      'filter-when': (this[`${filterPrefix}.when`] || []).map((p: { id: any }) => String(p.id)).join(','),
      'filter-dataset':  (this[`${filterPrefix}.dataset`] || []).map((p: { id: any }) => String(p.id)).join(','),
      'filter-dateStart': formatDate(dateStart),
      'filter-dateEnd': formatDate(dateEnd),
      'orderBy': '0',
      'page':  String(pageNo ?? 1),
      'recordsPerPage': this[`${UPDATES_SEARCH_TAB}.result.recordsPerPage`] ?? String(DEFAULT_RECORDS_PER_PAGE),
    }
    if(this[`${UPDATES_SEARCH_TAB}.result.sort`].length > 0) {
      params['orderBy'] = this[`${UPDATES_SEARCH_TAB}.result.sort`][0]?.id ?? '0';
    }
    return params
  },
  setDefaultTimeRangeForUpdatesSearch: function(this: ISearchStore, startDate: Date | null = null, endDate: Date | null = null) {
    const { start, end } = getYearDateRange();
    this[`${UPDATES_SEARCH_TAB}.facetFilter.dateStart`] = startDate || start;
    this[`${UPDATES_SEARCH_TAB}.facetFilter.dateEnd`] = endDate || end;
  },
  setUpdatesSearchResultData: function(this: ISearchStore, resultData: any) {
    this[`${UPDATES_SEARCH_TAB}.resultData`] = resultData;
    
    //for each filters, set the lookup fields
    const facetFilterMapping: Record<string, string> = {
      'publisher': 'publisher',
      'what': 'what',
      'dataset': 'dataset'
    };

    for (const [apiKey, stateKey] of Object.entries(facetFilterMapping)) {
      let options = resultData?.filters?.[apiKey]?.map((option: any) => ({
          id: option?.id?.toString() ?? '',
          text: option?.value?.toString() ?? ''
      })) ?? [];
      (this as any)[`lookUpOptions.${UPDATES_SEARCH_TAB}.facetFilter.${stateKey}`] = options;

      //selected options from api response
      let apiReturnedSelectedOptions = resultData?.filters?.[apiKey]?.filter((option: any) => option.selected).map((option: any) => ({
          id: option?.id?.toString() ?? '',
          text: option?.value?.toString() ?? ''
      })) || [];
      
      //selected search bar options
      const selectedSearchBarOptions = (this as any)[`${UPDATES_SEARCH_TAB}.${stateKey}`] || []; 

      //selected facet filter options
      let selectedFacetFilterOptions = (this as any)[`${UPDATES_SEARCH_TAB}.facetFilter.${stateKey}`] || [];  
      selectedFacetFilterOptions = selectedFacetFilterOptions.map((opt: any) => { //strip out count "(num)" from the facets selected options
        return { id: opt.id.toString(), text: opt.text.toString().replace(/\s*\(\s*\d+\s*\)\s*$/, '') };
      });

      //combine selected search bar and facet filter options
      //also remove duplicates from search bar selections
      const combinedSelectedOptions = [...selectedFacetFilterOptions, ...selectedSearchBarOptions.filter((prevOpt: any) => {
        return !selectedFacetFilterOptions.some((selOpt: any) => selOpt.id.toString() === prevOpt.id.toString());
      })];

      //remove duplicates from the (selected search bar + facet filter) selections
      //and combine selected options from api and (selected search bar + facet filter)
      //so we have selected options from api response + user selected options(search bar + facet filter) as api wlll not have all user selected options
      (this as any)[`${UPDATES_SEARCH_TAB}.facetFilter.${stateKey}`] = [...apiReturnedSelectedOptions, ...combinedSelectedOptions.filter((prevOpt: any) => {
        return !apiReturnedSelectedOptions.some((selOpt: any) => selOpt.id.toString() === prevOpt.id.toString());
      })];
    }
    (this as any)[`${UPDATES_SEARCH_TAB}.facetFilter.dateStart`] =  resultData.filters.dateStart ? resultData.filters.dateStart : null;
    (this as any)[`${UPDATES_SEARCH_TAB}.facetFilter.dateEnd`] =  resultData.filters.dateEnd ? resultData.filters.dateEnd : null;
    this[`${UPDATES_SEARCH_TAB}.result.sort`] = [
      UPDATES_SEARCH_SORT_OPTIONS.find(
        opt => opt.id === String(resultData.orderBy)
      ) ?? UPDATES_SEARCH_SORT_DEFAULT
    ];
    this[`${UPDATES_SEARCH_TAB}.result.page`] = resultData.page ?? 1;
  },
  
  async searchUpdates(this: ISearchStore, facetFilter: boolean = false, pageNo: number = 1) {
    try {
      this['searchResultActive'] = true; 
      this[`${UPDATES_SEARCH_TAB}.result.loading`] = true;
      const params = this.getUpdatesSearchParams(facetFilter, pageNo);
      const updatesSearchApiAbortController = this.addApiAbortController('updatesSearchApi');
      const results = await apiSearchResults(UPDATES_SEARCH_TAB, params, updatesSearchApiAbortController);
      if(results.status != 'error') {
        this.setUpdatesSearchResultData(results.data);
      }
      this[`${UPDATES_SEARCH_TAB}.result.loading`] = false;
    } catch (e: any) {
      if((e?.code??'') == 'ERR_CANCELED') return; //if the error is due to request cancellation, do nothing
      this[`${UPDATES_SEARCH_TAB}.result.loading`] = false;
      console.log('updates search failed');
    }
  },
  exportUpdatesSearchResults: async function(this: ISearchStore) {
    //@ts-ignore
    document.getElementById('updates-export-form')?.submit();
  }
}
